import { Component, OnDestroy, ViewChild, ElementRef, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { flatMap, takeUntil, tap, first, debounceTime, filter, map } from 'rxjs/operators';
import { StreamService } from '../services/stream.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { FormControl } from '@angular/forms';
import * as moment from 'moment';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { TaxonomyService, TenantService } from '../services';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute } from '@angular/router';

@Component({
    styles: [`
        .sourcesPanel {
            overflow-y: auto;
            height: calc(100vh - 137px);
        }
        .cdk-column-title.mat-column-title {
            width: calc(100vw - 900px);
            min-width: calc(100vw - 900px);
        }
        .cdk-column-published.mat-column-published {
            width: 55px;
            min-width: 55px;
            text-align: center;
        }
        .cdk-column-type.mat-column-type {
            width: 50px;
            min-width: 50px;
            text-align: center;
        }
        .cdk-column-itemsCount.mat-column-itemsCount {
            width: 85px;
            min-width: 85px;
            text-align: center;
        }
        .cdk-column-followersCount.mat-column-followersCount {
            width: 85px;
            min-width: 85px;
            text-align: center;
        }
        .cdk-column-aggregationStatus.mat-column-aggregationStatus {
            text-align: center;
            width: 55px;
            min-width: 55px;
        }
        .cdk-column-active.mat-column-active {
            width: 55px;
            min-width: 55px;
            text-align: center;
        }
        .cdk-column-actions.mat-column-actions {
            width: 65px;
            min-width: 65px;
        }
        mat-chip.small-chip {
            margin: 0px;
            min-height: 25px;
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar style="height: 70px;">
                <h3>{{isTopics ? 'Topic' : 'Stream'}}s</h3>
                <span fxFlex></span>
                <mat-form-field style="font-size: 16px; font-weight: 400; width: 300px;">
                    <input matInput placeholder="Search Term" [formControl]="searchControl">
                </mat-form-field>
                <mat-form-field fxFlex="auto" style="font-size: 16px; font-weight: 400; padding-left: 20px">
                    <mat-chip-list #chipList>
                        <mat-chip *ngFor="let tag of searchTags | async" color="primary" 
                                  (removed)="removeTag(tag)"
                                  class="small-chip">
                            {{tag.label}}
                            <mat-icon matChipRemove>cancel</mat-icon>
                        </mat-chip>
                        <input placeholder="Search Tags"
                               #tagInputCtrl
                               [formControl]="tagCtrl"
                               [matAutocomplete]="auto"
                               [matChipInputFor]="chipList"
                               (matChipInputTokenEnd)="add($event)">
                    </mat-chip-list>
                    <mat-autocomplete #auto="matAutocomplete" (optionSelected)="tagSelected($event)" [autoActiveFirstOption]="true">
                        <mat-option *ngFor="let tag of filteredTags" [value]="tag">{{tag.label}}</mat-option>
                    </mat-autocomplete>
                </mat-form-field>
                <mat-select placeholder="Category" [formControl]="categoryControl" style="max-width: 110px; padding-right: 15px; padding-left: 15px;  font-size: 14px;">
                    <mat-option></mat-option>
                    <mat-option *ngFor="let category of categories" [value]="category.id">{{category.title}}</mat-option>
                </mat-select>
                <button *ngIf="!isTopics" mat-button color="primary" [routerLink]="['./create']">Add Source</button>
                <button *ngIf="isTopics" mat-button color="primary" [routerLink]="['/topics/create']">Add Topic</button>
            </mat-toolbar>
            <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
            <div *ngIf="!emptySearch"
                 class="sourcesPanel"
                 infiniteScroll 
                 [infiniteScrollThrottle]="300"
                 [infiniteScrollDistance]="1"
                 [scrollWindow]="false"
                 (scrolled)="nextPage()">
                <table mat-table
                    matSort 
                    (matSortChange)="sort($event)"
                    [matSortDisableClear]="true "
                    [matSortActive]="params?.value?.sort" 
                    [matSortDirection]="params?.value?.sortDirection" 
                    *ngIf="!noTouching" 
                    [dataSource]="sources" 
                    style="width: 100%;">
                    <ng-container matColumnDef="title">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="titleLower">Title</th>
                        <td mat-cell *matCellDef="let row">{{row.title}}</td>
                    </ng-container>
                    <ng-container matColumnDef="published">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="published" start="desc" style="padding-left: 15px;">Published</th>
                        <td mat-cell *matCellDef="let row">
                            <mat-slide-toggle [checked]="row.published ? true : false" (change)="togglePublished(row)">
                            </mat-slide-toggle>
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="type">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="type" style="padding-left: 15px;">Type</th>
                        <td mat-cell *matCellDef="let row">
                            <mat-icon [fontSet]="types[row.type].set" 
                                      [fontIcon]="types[row.type].icon"
                                      [matTooltip]="types[row.type].label"></mat-icon>
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="itemsCount">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="itemsCount" start="desc" style="padding-left: 33px;">Items</th>
                        <td mat-cell *matCellDef="let row">{{row.itemsCount}}</td>
                    </ng-container>
                    <ng-container matColumnDef="followersCount">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="followersCount">Followers</th>
                        <td mat-cell *matCellDef="let row">{{row.followersCount}}</td>
                    </ng-container>
                    <ng-container matColumnDef="created">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="created" start="desc">Created</th>
                        <td mat-cell *matCellDef="let row">{{row.created | date:'shortDate'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="lastSuccess">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="status.lastSuccess" start="desc" style="padding-left: 15px;">Last Success</th>
                        <td mat-cell *matCellDef="let row" [matTooltip]="row.status.lastSuccess" style="padding-left: 15px;">{{row.status.lastSuccess | date:'shortDate'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="lastFailure">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="status.lastFailure" start="desc" style="padding-left: 15px;">Last Error</th>
                        <td mat-cell *matCellDef="let row" [matTooltip]="row.status.lastFailure" style="padding-left: 15px;">{{row.status.lastFailure | date:'shortDate'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="aggregationStatus">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="status" style="padding-left: 15px;">Status</th>
                        <td mat-cell *matCellDef="let row">
                            <span *ngIf="row.status.inError" 
                                [matTooltip]="row.status.lastErrorMessage"
                                style="color: red;">
                                <mat-icon>clear</mat-icon>
                            </span>
                            <span *ngIf="!row.status.inError" [matTooltip]="aggregatedLabel(row.status.lastAttempted)">
                                <mat-icon>done</mat-icon>
                            </span>
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="active">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="settings.aggregationDisabled" start="desc" style="padding-left: 15px;">Active</th>
                        <td mat-cell *matCellDef="let row">
                            <mat-slide-toggle [checked]="row.settings.aggregationDisabled ? false : true" (change)="toggleAggregation(row)">
                            </mat-slide-toggle>
                        </td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef></th>
                        <td mat-cell *matCellDef="let row">
                            <mat-menu #menu="matMenu">
                                <a mat-menu-item [routerLink]="[row.id]">
                                    <mat-icon>build</mat-icon>
                                    <span>Edit</span>
                                </a>
                                <button mat-menu-item (click)="delete(row)"><mat-icon>clear</mat-icon>Delete</button>
                            </mat-menu>
                            <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                                <mat-icon>settings</mat-icon>
                            </button>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="columns"></tr>
                    <tr mat-row *matRowDef="let row; columns: columns;"></tr>
                </table>
                <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                    <spinner></spinner>
                </div>
            </div>
            <div *ngIf="emptySearch" fxLayoutAlign="center center">
                <h3>No sources match your search criteria. <button mat-button (click)="clearSearch()">Clear search.</button></h3>
            </div>
        </div>
    `
})
export class StreamListComponent implements OnInit, OnDestroy {
    noTouching: boolean;
    noTouchingPaging: boolean;
    searchControl = new FormControl();
    columns;
    private cleanupTime = new Subject<void>();
    types = StreamService.types;
    params = new BehaviorSubject<any>(null);
    sources = new BehaviorSubject<any[]>([]);
    lastFetched: boolean;
    categories = [];
    categoryControl = new FormControl();
    @ViewChild('tagInputCtrl') tagInput: ElementRef<HTMLInputElement>;
    tagCtrl = new FormControl();    
    filteredTags = [];
    searchTags = new BehaviorSubject<any[]>(null);
    streamTags: any;
    emptySearch: boolean;
    categoryId: any;
    isTopics: boolean;

    constructor(private snackBar: MatSnackBar,
                private dialog: MatDialog,
                private route: ActivatedRoute, 
                private streamService: StreamService,
                private taxonomyService: TaxonomyService,
                private tenantService: TenantService) {}
    
    ngOnInit(): void {
        if (this.route.snapshot.data.isTopics === true) {
            this.isTopics = true;
            this.columns = ['title', 'itemsCount', 'followersCount', 'created', 'published', 'actions'];
        } else {
            this.columns = ['title', 'published', 'type', 'itemsCount', 'created', 'lastSuccess', 'lastFailure', 'aggregationStatus', 'active', 'actions'];
        }

        this.tenantService.getStreamTags()
        .pipe(
            tap(tags => this.streamTags = tags),
            filter(() => this.route.snapshot.queryParams.tag),
            map(() => this.streamTags.find(tag => tag.id === this.route.snapshot.queryParams.tag)),
            filter(tag => !!tag),
            map(tag => (this.searchTags.value || []).concat([tag]))
        )
        .subscribe(updated => this.searchTags.next(updated));

        this.taxonomyService.getTenantTaxonomy(true, true, true)
        .pipe(map(categories => categories.concat(categories.flatMap(cat => cat.subcategories)).sort((a, b) => (a.title?.toLowerCase() > b.title?.toLowerCase()) ? 1 : ((b.title?.toLowerCase() > a.title?.toLowerCase()) ? -1 : 0))))
        .subscribe(sortedCategories => this.categories = sortedCategories);

        this.params
        .pipe(
            filter(params => !!params),
            flatMap(params => this.streamService.tenantStreamSearch(params)),
            tap(() => this.noTouchingPaging = false),
            tap(sources => this.lastFetched = sources.last),
            tap(response => this.emptySearch = response.first && response.last && !response.numberOfElements && this.params.value.term?.trim().length),
            takeUntil(this.cleanupTime)
        )
        .subscribe(sources => this.sources.next(this.sources.value.concat(sources.content)));

        this.searchTags
        .pipe(
            filter(tags => !!tags),
            map(tags => tags.length ? tags.map(tag => tag.id).join(',') : undefined),
            takeUntil(this.cleanupTime )
        ).subscribe(tagIds => this.nextParams({ tagIds }));

        this.tagCtrl.valueChanges
        .pipe(
            map(() => this.tagCtrl.value?.toLowerCase ? this.tagCtrl.value.toLowerCase() : ''),
            takeUntil(this.cleanupTime)
        ).subscribe(val => this.filteredTags = this.availableTags().filter(tag => tag.label.toLowerCase().includes(val)));

        this.searchControl.valueChanges.pipe(debounceTime(500), takeUntil(this.cleanupTime))
        .subscribe(term => this.nextParams({term, page: 0}));

        this.categoryControl.valueChanges
        .pipe(
            filter(categoryId => categoryId !== this.categoryId),
            tap(categoryId => this.categoryId = categoryId),
            map(categoryId => categoryId ? [categoryId] : undefined),
            takeUntil(this.cleanupTime)
        ).subscribe(categoryIds => this.nextParams({ categoryIds, page: 0}));

        if (!this.route.snapshot.queryParams.tag) {
            this.nextParams({});
        }
    }
    
    nextParams(newParams, preserveSources = false):void {
        if (!preserveSources) {
            this.sources.next([]);
        }
        this.noTouchingPaging = !this.noTouching && true;
        const coreParams = { sort: 'titleLower', sortDirection: 'asc', filterEmpty: true, page: 0, rows: 40 };
        const defaultParams = this.isTopics ? Object.assign({ type: 'COLLECTION' }, coreParams) : Object.assign({ hasBriefingEnabled: false,  excludeTypes: 'COLLECTION' }, coreParams);
        this.params.next(Object.assign({}, this.params.value || defaultParams, newParams));
    }

    availableTags = (): any[] => this.streamTags?.filter(tag => !this.searchTags.value?.includes(tag.id)) || [];

    clearSearch() {
        this.searchControl.setValue('');
        this.searchControl.updateValueAndValidity();
        this.emptySearch = false;
    }

    nextPage = (): void => this.nextParams({ page: this.params.value.page + 1}, true);

    sort = (sort: Sort): void => this.nextParams({ page: 0, sort: sort.active, sortDirection: sort.direction });

    aggregatedLabel = (aggregationDate): String => 'aggregated ' + moment(aggregationDate).fromNow();

    toggleAggregation(stream): void {
        stream.settings.aggregationDisabled = !stream.settings.aggregationDisabled;
        this.streamService.updateStream(stream)
        .subscribe(() => this.snackBar.open(`Aggregation ${stream.settings.aggregationDisabled ? 'disabled' : 'enabled' }`, 'OK', { duration: 3000}));
    }

    togglePublished(stream): void {
        stream.published = !stream.published;
        this.streamService.updateStream(stream)
        .subscribe(() => this.snackBar.open(`Source ${stream.published ? 'published' : 'unpublished' }`, 'OK', { duration: 3000}));
    }

    add(event: MatChipInputEvent): void {
        if (event.value?.length) {
            const existingMatch = this.availableTags().find(tag => tag.label.toLowerCase() === event.value.toLocaleLowerCase());
            
            if (existingMatch && !this.searchTags.value?.find(tagId => tagId === existingMatch.id)) {
                this.searchTags.next(this.searchTags.value.concat(existingMatch));
            }

            if (event.input) {
                event.input.value = '';
            }

            this.tagCtrl.setValue(null);
        }
    }

    tagSelected(event: MatAutocompleteSelectedEvent): void {
        this.searchTags.next((this.searchTags.value || []).concat(event.option.value));
        this.tagInput.nativeElement.value = '';
        this.tagCtrl.setValue(null);
    }

    removeTag(tag): void {
        const val = this.searchTags.value || [];
        val.splice(val.indexOf(tag), 1);
        this.searchTags.next(val);
    }

    delete(topic): void {
        this.dialog.open(ConfirmationDialogComponent, { 
        data: {
            title: `Delete ${topic.title}?`, 
            actionLabel: 'Delete'}
        }).afterClosed()
        .pipe(
            filter(doIt => !!doIt),
            flatMap(() => this.streamService.deleteStream(topic.id).pipe(first())),
            tap(() => this.snackBar.open(`${topic.title} has been deleted.`, 'OK', { duration: 3000})),
        ).subscribe(() => this.nextParams({ page: 0}));
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}